import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/authApi';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setOnUnauthorized,
  setTokens,
} from '../api/axiosClient';
import type {
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  RegisterClientRequest,
  RegisterPilotRequest,
  ResetPasswordRequest,
} from '../types/auth.types';
import type { UserRole } from '../types/api.types';
import type { ClientProfile } from '../types/client.types';
import type { PilotProfile } from '../types/pilot.types';
import type { User } from '../types/user.types';

type AuthContextValue = {
  user: User | null;
  clientProfile: ClientProfile | null;
  pilotProfile: PilotProfile | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: UserRole | null;
  login: (payload: LoginRequest) => Promise<User>;
  registerClient: (payload: RegisterClientRequest) => Promise<User>;
  registerPilot: (payload: RegisterPilotRequest) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  forgotPassword: (payload: ForgotPasswordRequest) => Promise<ForgotPasswordResponse>;
  resetPassword: (payload: ResetPasswordRequest) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
  const [pilotProfile, setPilotProfile] = useState<PilotProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(() => getAccessToken());
  const [isLoading, setLoading] = useState<boolean>(Boolean(getAccessToken()));

  const clearSession = useCallback(() => {
    clearTokens();
    setAccessToken(null);
    setUser(null);
    setClientProfile(null);
    setPilotProfile(null);
  }, []);

  useEffect(() => {
    setOnUnauthorized(clearSession);
    return () => setOnUnauthorized(null);
  }, [clearSession]);

  // Hydrate session and profile from /auth/me so post-login redirects always reflect backend state.
  const hydrateFromMe = useCallback(async (): Promise<User | null> => {
    try {
      const response = await authApi.me();
      setUser(response.data.user);
      setClientProfile((response.data.client_profile as ClientProfile | null | undefined) ?? null);
      setPilotProfile((response.data.pilot_profile as PilotProfile | null | undefined) ?? null);
      setAccessToken(getAccessToken());
      return response.data.user;
    } catch {
      clearSession();
      return null;
    }
  }, [clearSession]);

  useEffect(() => {
    let active = true;
    if (!getAccessToken() && !getRefreshToken()) {
      setLoading(false);
      return () => undefined;
    }
    void hydrateFromMe().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [hydrateFromMe]);

  const persistSession = (nextAccess: string, nextRefresh: string) => {
    setTokens(nextAccess, nextRefresh);
    setAccessToken(nextAccess);
  };

  const value = useMemo<AuthContextValue>(() => ({
    user,
    clientProfile,
    pilotProfile,
    accessToken,
    isAuthenticated: Boolean(user && accessToken),
    isLoading,
    role: user?.role || null,
    login: async (payload) => {
      const response = await authApi.login(payload);
      persistSession(response.data.access_token, response.data.refresh_token);
      const me = await hydrateFromMe();
      return me ?? response.data.user;
    },
    registerClient: async (payload) => {
      const response = await authApi.registerClient(payload);
      persistSession(response.data.access_token, response.data.refresh_token);
      const me = await hydrateFromMe();
      return me ?? response.data.user;
    },
    registerPilot: async (payload) => {
      const response = await authApi.registerPilot(payload);
      persistSession(response.data.access_token, response.data.refresh_token);
      const me = await hydrateFromMe();
      return me ?? response.data.user;
    },
    logout: async () => {
      try {
        await authApi.logout();
      } catch {
        // Stateless JWT logout — clear the client session regardless of server response.
      } finally {
        clearSession();
      }
    },
    refreshUser: hydrateFromMe,
    forgotPassword: async (payload) => {
      const response = await authApi.forgotPassword(payload);
      return response.data;
    },
    resetPassword: async (payload) => {
      await authApi.resetPassword(payload);
    },
  }), [accessToken, clearSession, clientProfile, hydrateFromMe, isLoading, pilotProfile, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
